'use client';

import {
	ArrowBackIconLight,
	ContentWrap,
	Css,
	Responsive,
	useIntersectionObserver,
} from '@/ui';
import styled from '@emotion/styled';
import { ISiteSectionEntry } from '@move-frontend/contentful';
import { useRef } from 'react';
import { usePathname } from 'next/navigation';
import defaultHeaderImage from '../../../assets/defaultHeaderImage.jpg';
import { Color } from '../../../styles/Color';
import { ButtonLabel } from '../button/Button';
import OxdLogo from '../../../assets/oxd-logo.svg';
import { theme } from '../../../styles/theme';
import { Z_INDEX_BASE, Z_INDEX_HEADER_TEXT } from '../../../styles/zIndex';
import { ICON_GAP_TRANSITION } from '../../../styles/animation/iconGapTransition';
import {
	HeaderContentWrap,
	HeaderVideoOverlay,
	HeaderWrapper,
} from './Header.styles';
import { Topbar } from '../top-bar/Topbar';
import { RouterLink } from '../link/RouterLink';
import { PageRoutes } from '../../../features/app/pages/domain/constants/PageRoutes';

export interface IHeaderProps {
	backgroundImage?: string;
	backgroundVideo?: string;
	backgroundPosition?: string;
	backgroundVideoFormat?: string;
	title?: string;
	titleTop?: boolean;
	backLink?: string;
	siteSection?: ISiteSectionEntry;
}

export default function Header({
	backgroundImage,
	backgroundVideo,
	backgroundPosition,
	backgroundVideoFormat,
	backLink,
	title,
	titleTop,
	siteSection,
}: IHeaderProps) {
	const ref = useRef<HTMLDivElement>(null);
	const image = backgroundImage || defaultHeaderImage;
	const intersect = useIntersectionObserver(ref, {
		threshold: 1,
	});
	const isTopbarSticky = intersect?.isIntersecting === false;

	const pathname = usePathname();
	const oxdThemed = pathname === `${PageRoutes.OxdAirport}/`;

	return (
		<HeaderWrapper
			backgroundImage={image}
			backgroundPosition={backgroundPosition}
		>
			{backgroundVideo && (
				<StyledHeaderVideoOverlay>
					<StyledHeaderVideo loop controls={false} muted autoPlay playsInline>
						<source src={backgroundVideo} type={backgroundVideoFormat} />
					</StyledHeaderVideo>
				</StyledHeaderVideoOverlay>
			)}
			<HeaderContentWrap ref={ref}>
				<Topbar onBackgroundScheme="dark" siteSection={siteSection} />
				<HeaderContent titleTop={titleTop} isTopbarSticky={isTopbarSticky}>
					{backLink && (
						// 	eslint-disable-next-line @typescript-eslint/ban-ts-comment
						// @ts-ignore
						<BackButton as={RouterLink} href={backLink}>
							<ArrowBackIconLight />
							<ButtonLabel>Back</ButtonLabel>
						</BackButton>
					)}
					{oxdThemed ? <StyledOxdLogo /> : <PageTitle>{title}</PageTitle>}
				</HeaderContent>
			</HeaderContentWrap>
		</HeaderWrapper>
	);
}

const HEADER_DESKTOP_HEIGHT = '500px';
const HEADER_MOBILE_HEIGHT = '248px';

const StyledHeaderVideoOverlay = styled(HeaderVideoOverlay)`
	&::after {
		max-height: ${HEADER_DESKTOP_HEIGHT};
		@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
			max-height: ${HEADER_MOBILE_HEIGHT};
		}
	}
`;

const StyledHeaderVideo = styled.video`
	position: absolute;
	left: 0;
	height: 100vh;
	width: 100%;
	object-fit: cover;
	max-height: ${HEADER_DESKTOP_HEIGHT};

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		height: ${HEADER_MOBILE_HEIGHT};
	}
`;

const StyledOxdLogo = styled(OxdLogo)`
	width: 180px;
	height: 60px;
	@media (${Responsive.getMediaQueryForBreakpoint('tabletXL')}) {
		width: 135px;
		height: 45px;
	}
`;

const HeaderContent = styled(ContentWrap)<{
	titleTop?: boolean;
	isTopbarSticky?: boolean;
}>`
	display: flex;
	flex: 1;
	flex-direction: column;
	justify-content: ${({ titleTop }) => (titleTop ? 'flex-start' : 'flex-end')};
	margin-top: ${({ titleTop }) =>
		titleTop ? `${Css.pxToRem(50)}` : 'initial'};
	width: 100%;

	h3 {
		line-height: normal;
	}
	margin-top: ${({ isTopbarSticky }) =>
		isTopbarSticky ? `${Css.pxToRem(114)}` : 0};

	margin-bottom: ${Css.pxToRem(80)};

	@media (${Responsive.getMediaQueryForBreakpoint('tabletXL')}) {
		justify-content: ${({ titleTop }) => (titleTop ? 'center' : 'flex-end')};
		margin-bottom: ${({ titleTop }) =>
			titleTop ? `${Css.pxToRem(180)}` : `${Css.pxToRem(80)}`};
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		margin-bottom: ${Css.pxToRem(48)};
		margin-top: ${Css.pxToRem(64)};

		h3 {
			font-size: ${Css.pxToRem(40)};
		}
	}
`;

const BackButton = styled.button`
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: ${theme.button?.iconSeparator};
	text-decoration: none;
	font-weight: 500;
	color: ${Color.OnPrimarySurface};
	margin-bottom: ${Css.pxToRem(10)};
	width: fit-content;

	&:hover {
		cursor: pointer;
		color: ${Color.OnPrimarySurface};

		svg {
			transform: translateX(-${theme.button?.iconSeparator});
		}
	}

	svg {
		path {
			fill: ${Color.OnPrimarySurface};
		}
		transition: ${`transform ${ICON_GAP_TRANSITION}`};
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		z-index: ${Z_INDEX_BASE};
	}
`;

const PageTitle = styled.h3`
	color: ${Color.OnPrimarySurface};
	margin-bottom: 0;
	z-index: ${Z_INDEX_HEADER_TEXT};
	display: -webkit-box;
	-webkit-line-clamp: 5;
	-webkit-box-orient: vertical;
	overflow: hidden;
	text-overflow: ellipsis;
`;
